import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { usePublisherReviewService } from '../../contexts/PublisherReviewServiceContext';
import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { ContentEditorService, LoadedDocumentHandle } from '../../services/ContentEditorService';
import { InlineCommentManager } from '../../services/InlineCommentManager';
import { log } from '../../loggers/Logger';
import { isNotFoundError } from './isNotFoundError';
import { ViewTarget, parseViewTarget } from '../v2-content-viewer/parseViewTarget';
import { useLoadedDocumentHandle } from './useLoadedDocumentHandle';

/** Shown when the URL names a workflow kind this route can't render. */
const UNSUPPORTED_VERSION_MESSAGE = 'This version cannot be viewed here.';

/** The current load state for the read-only view page. */
export interface ViewContentLoadState {
  /** The loaded document, or undefined while loading or on error. */
  readonly loadedDocumentHandle: LoadedDocumentHandle | undefined;
  /** True until the initial open resolves (or fails). */
  readonly isLoading: boolean;
  /** Display-ready error message, or undefined when no error or when not found. */
  readonly errorMessage: string | undefined;
  /** True when the content is missing or the viewer is not authorized to see it. */
  readonly isNotFound: boolean;
  /** True when viewing a published version rather than the draft. */
  readonly isPublicationView: boolean;
}

/**
 * Opens the content on the editor service for the given {@link ViewTarget}.
 * A `read-publication` target opens the published version read-only with no
 * comment manager — publications have no comment surface. A `read-draft`
 * target opens the draft read-only and loads its inline comments, which needs
 * the `draftWorkflowId` from the loaded handle, so that step runs strictly
 * after the open resolves.
 *
 * @param signal Cancellation flag checked between async steps. If the caller
 *   flips `signal.cancelled` before the second step starts, the comment manager
 *   is never opened — preventing a leak when the effect's cleanup fires mid-flight.
 * @throws if the service rejects, or if the draft open does not produce a draft
 *   handle (the throw surfaces the unexpected shape rather than silently skipping
 *   the comment load).
 */
async function openContentAndComments(
  service: ContentEditorService,
  commentManager: InlineCommentManager,
  contentId: string,
  target: Extract<ViewTarget, { kind: 'read-draft' | 'read-publication' }>,
  signal: { cancelled: boolean },
): Promise<void> {
  // Published version: open read-only, no comments.
  if (target.kind === 'read-publication') {
    await service.open(contentId, { kind: 'read-publication', workflowId: target.workflowId });
    return;
  }

  // Draft: open read-only, then load its inline comments.
  await service.open(contentId, { kind: 'read-draft' });
  // Cleanup may have already closed the service. Bail out before we open the
  // comment manager that nobody will close.
  if (signal.cancelled) return;
  const handle = service.loadedDocumentHandle;
  if (!handle || handle.document.kind !== 'draft') {
    throw new Error('Editor service did not produce a draft handle after read-draft open.');
  }
  const draftWorkflowId = handle.document.workflow.draftWorkflowId;
  await commentManager.open(contentId, { kind: 'draft', workflowId: draftWorkflowId });
}

/**
 * Opens the content for the `:contentId` route param on the
 * {@link ContentEditorService} for read-only viewing, and binds the caller to
 * the service's loaded document. The query string selects what to open: no
 * `workflowId` loads the draft and its inline comments; `workflowId` with
 * `mode=publication` loads that published version; any other versioned target
 * is unsupported and surfaces an error without opening. On unmount or input
 * change, both services are closed.
 */
export function useViewContentLoad(): ViewContentLoadState {
  // Services + route input
  const service = useContentEditorService();
  const commentManager = useInlineCommentManager();
  const qualityCheckService = useQualityCheckService();
  const publisherReviewService = usePublisherReviewService();
  const { contentId } = useParams<{ contentId: string }>();
  const { search } = useLocation();

  // Decode what the URL asks us to open. Memoized so its reference is stable
  // per URL — safe to use directly as an effect dep. parseViewTarget throws on
  // a non-numeric workflowId; let it propagate to the router error boundary.
  const target = useMemo(() => parseViewTarget(search), [search]);

  // States
  const [isLoading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isNotFound, setNotFound] = useState(false);
  const loadedDocumentHandle = useLoadedDocumentHandle();

  // When contentId/target changes, open the content once and (for drafts)
  // load its inline comments; close both on unmount / input change. open and
  // closeAndDiscard are expensive, so we never re-run them on re-renders.
  useEffect(() => {
    // An unsupported target never opens anything, so there is nothing to load
    // or clean up — just report it and stop.
    if (target.kind === 'unsupported') {
      setLoading(false);
      setErrorMessage(UNSUPPORTED_VERSION_MESSAGE);
      return;
    }

    // Shared cancel flag: read inside the async IIFE and by the helper between
    // its two awaits; flipped to true by the cleanup below.
    const cancelSignal = { cancelled: false };

    setLoading(true);
    setErrorMessage(undefined);
    setNotFound(false);

    // Both scan services outlive this page, so drop the previous content's scan
    // state before this one loads — otherwise its results and still-running poll
    // cycle surface against the content opened here.
    qualityCheckService.resetState();
    publisherReviewService.resetState();

    (async () => {
      try {
        await openContentAndComments(service, commentManager, contentId, target, cancelSignal);
      } catch (e) {
        if (cancelSignal.cancelled) return;
        log.error({ err: e, contentId }, 'Failed to load content');
        // A forbidden/missing content renders the not-found page instead of a
        // generic error, so surface it as not-found rather than a message.
        if (isNotFoundError(e)) {
          setNotFound(true);
          return;
        }
        setErrorMessage(
          `Failed to load content: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      } finally {
        if (!cancelSignal.cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelSignal.cancelled = true;
      // Stop any in-flight quality check poll before the content closes below;
      // otherwise it keeps polling a document that is no longer loaded.
      qualityCheckService.resetState();

      // Fire-and-forget: React cleanup must be synchronous. Log but swallow
      // errors so unmount doesn't produce unhandled rejections. Both closes are
      // safe when nothing is open (e.g., the publication path never opens
      // comments, or when cleanup beats `commentManager.open`).
      commentManager.close().catch((e) => {
        log.warn({ error: e, contentId }, 'Failed to close inline comments on unmount');
      });
      service.closeAndDiscard().catch((e) => {
        log.warn({ error: e, contentId }, 'Failed to close content on unmount');
      });
    };
  }, [service, commentManager, qualityCheckService, publisherReviewService, contentId, target]);

  return {
    loadedDocumentHandle,
    isLoading,
    errorMessage,
    isNotFound,
    isPublicationView: target.kind === 'read-publication',
  };
}
